
import {
  computed,
  defineComponent,
  onMounted,
  PropType,
  ref,
  toRef,
  watch,
  shallowRef,
} from "vue";
import { IBulkDelete } from "@/interfaces/iPageModel";
import Datatable from "@/components/kt-datatable/KTDatatable.vue";
import { TranslationService } from "@/core/services/TranslationService";
import { INNER_PAGE_ROWS_PER_PAGE, usePagination } from "@/helpers/pagination";
import { FormatMethods } from "@/helpers/data-format";
import { UserAPI } from "@/services/api/User.service";
import { SiteApi } from "@/services/api/Site.service";
import VZDeleteModal from "@/components/content/VZDeleteModal.vue";
import {
  userSiteTableBulkDelete,
  userSiteTableHeaders,
  USER_SITE_LOOKUP,
} from "../dataTableOptions";
import { IUser, IUserSite, IUserSiteCreate } from "@/interfaces/iUser";
import { ISite } from "@/interfaces/iSite";
import { IAddEditFormModel } from "@/interfaces/iAddEditFormModel";
import { getLookups } from "@/services/api/Lookup.service";
import * as Yup from "yup";
import { useToast } from "vue-toastification";
import { ITabConfig } from "@/interfaces/iTabConfig";
import { SITE_TABS } from "@/views/site/siteDataTableOptions";
import PreviewLink from "@/components/typography/PreviewLink.vue";
import SiteDetailModal from "@/views/site/modal/SiteDetailModal.vue";
import { hasPermission } from "@/core/services/permission.service";
import AddEditModal from "@/components/modals/forms/AddEditModal.vue";
import { Modal } from "bootstrap";
import { debounce } from "lodash";
import { useStore } from "vuex";
import { ICurrentUser } from "@/interfaces/iCurrentUser";
export default defineComponent({
  name: "UserSite",
  props: {
    data: { type: Object as PropType<IUser>, required: true },
    openDrawer: { type: Boolean, default: false },
    isActive: { type: Boolean, default: false },
  },
  components: {
    Datatable,
    VZDeleteModal,
    PreviewLink,
    SiteDetailModal,
    AddEditModal,
  },
  setup(props) {
    const t = (key: string) => {
      return TranslationService.translate(key);
    };
    const store = useStore();
    const toast = useToast();
    const currentUser = computed(() => {
      return store.getters.currentUser as ICurrentUser;
    });
    const dataProp = toRef(props, "data");
    const openDrawer = toRef(props, "openDrawer");
    const { page, total } = usePagination();
    const rowsPerPage = ref(INNER_PAGE_ROWS_PER_PAGE);
    const loading = ref(false);
    const showAddSiteRole = ref(false);
    const defaultFilter = ref({ status: "ACTIVE" });
    const showDeleteDialog = ref<any>(false);
    const showDetailDialog = ref<any>(false);
    const showSearch = true;
    const tableData = ref<IUserSite[]>([]);
    const allTableData = ref<IUserSite[]>([]);
    const selectedForAction = ref<Partial<IUserSite>>({});
    const selectedForSite = ref<Partial<ISite>>({});
    const localPreference = ref({});
    const bulkDelete = ref<IBulkDelete>(userSiteTableBulkDelete());
    const tableHeader = ref(userSiteTableHeaders());
    // const tableFilter = ref({});
    let userOptions: { label: any; value: string }[] = [];
    let roleOptions: { label: any; value: string }[] = [];
    const siteRoleOptions = ref<{ label: string; value: string }[]>();
    const siteAssignedMsg = t("pages.user.siteAssignedMsg");
    const cannotBeUnassign = t("pages.site.cannotBeUnassign");
    const tabs = shallowRef<ITabConfig[]>(SITE_TABS());
    const format = computed(() => {
      return FormatMethods;
    });
    const Datatable = ref();
    onMounted(async () => {
      // await refreshList();
      loading.value = true;
      await getOptions();
    });
    const getOptions = async () => {
      await getLookups(`security/filter/ROLES`)
        .then((res) => {
          roleOptions = [];
          res.map((data) => {
            roleOptions.push({
              label: data.text,
              value: data.value,
            });
          });
        })
        .catch((err) => {
          console.error(err);
        });
      await getLookups(USER_SITE_LOOKUP.url)
        .then((res) => {
          userOptions = [];
          res.map((data) => {
            userOptions.push({
              label: data.text,
              value: data.value,
            });
          });
        })
        .catch((err) => {
          console.error(err);
        });
      const siteIds = allTableData.value.map((item) => Number(item.siteId));
      const filteredValues = userOptions.filter(
        (item) => !siteIds.includes(Number(item.value))
      );
      formData.value.model[0].options = filteredValues;
      if (filteredValues.length > 0) {
        formData.value.model[1].options = roleOptions;
      } else {
        formData.value.model[1].options = [];
      }
    };
    let formData = ref<IAddEditFormModel>({
      header: "Site",
      page: "Site",
      edit: false,
      data: null,
      label: [{ addHeader: t("pages.user.assignSite") }],
      validationSchema: Yup.object({
        siteId: Yup.string().required(
          t("pages.user.site") + " " + t("common.isRequired")
        ),
        roleId: Yup.string().required(
          t("pages.user.role") + " " + t("common.isRequired")
        ),
      }),
      model: [
        {
          name: "siteId",
          label: t("pages.site.site"),
          type: "list",
          required: true,
          showOnlyInEdit: false,
          disabled: false,
          placeholder: "Please select branch",
          options: userOptions,
        },
        {
          name: "roleId",
          label: t("pages.site.role"),
          type: "list",
          required: true,
          showOnlyInEdit: false,
          disabled: false,
          placeholder: "Please select role",
          options: roleOptions,
        },
      ],
    });
    const userSiteModelData = ref<IAddEditFormModel>({
      header: "Site",
      page: "Site",
      edit: false,
      data: null,
      label: [{ addHeader: t("pages.user.assignRole") }],
      validationSchema: Yup.object({
        roleId: Yup.string().required(
          t("pages.user.role") + " " + t("common.isRequired")
        ),
      }),
      model: [
        {
          name: "roleId",
          label: t("pages.site.role"),
          type: "list",
          required: true,
          showOnlyInEdit: false,
          disabled: false,
          placeholder: "Please select role",
          options: siteRoleOptions.value ?? [],
        },
      ],
    });
    const computedUserSiteModalData = computed(() => {
      return {
        ...userSiteModelData.value,
        model: userSiteModelData.value.model.map((model) => {
          if (model.name === "roleId") {
            return { ...model, options: siteRoleOptions.value ?? [] };
          }
          return model;
        }),
      };
    });
    const syncQuery = async (params?: Record<string, number | string>) => {
      queryUpdate(dataProp.value.webId, params);
    };
    let permissionModel = ref<any>({
      create: "VYZOR:USERS:USERS:CREATE",
      delete: "VYZOR:USERS:USERS:DELETE",
      update: "VYZOR:USERS:USERS:UPDATE",
    });
    const queryUpdate = async (
      webId: number,
      params?: Record<string, number | string>
    ) => {
      try {
        loading.value = true;
        if (!params || !params.sort) {
          params = {
            ...params,
            sort: "createdDate,desc",
          };
        }
        const data = await UserAPI.getInstance().getUserSites({
          ...params,
          userId: dataProp.value.webId,
        });
        const allData = await UserAPI.getInstance().getUserSites({
          status: "ACTIVE",
          size: 10000,
          userId: dataProp.value.webId,
        });
        localPreference.value = { ...params };
        tableData.value = [...data.content];
        allTableData.value = [...allData.content];
        total.value = data.totalElements;
        rowsPerPage.value = data.size;
        page.value = ++data.number;
        await getOptions();
      } finally {
        loading.value = false;
      }
    };
    const refreshList = () => {
      showDeleteDialog.value = false;
      queryUpdate(dataProp.value.webId, { ...localPreference.value });
    };
    const openDetailModal = async (site) => {
      showDetailDialog.value = true;
      selectedForSite.value = {};
      const sites = await SiteApi.getInstance().getSite(site.siteId);
      selectedForSite.value = sites;
    };
    const roles =
      currentUser.value?.currentUserSite?.map((role) => role.roleName) || [];
    const isAdmin = roles.includes("Admin");
    const openDeleteDialog = (user) => {
      if (!isAdmin && tableData.value.length === 1) {
        toast.error(cannotBeUnassign);
      } else {
        selectedForAction.value = user;
        showDeleteDialog.value = true;
      }
    };
    const submit = async (reqData) => {
      try {
        const payload: IUserSiteCreate = {
          ...reqData,
          userAccountId: dataProp.value.webId,
        };
        await UserAPI.getInstance().addSite(payload);
        toast.success(siteAssignedMsg);
        refreshList();
      } catch (e) {
        //
      }
    };
    const openRoleModal = async (site) => {
      try {
        // site/userSites/roles?siteId=1229006
        selectedForSite.value = site;
        const data = await getLookups(`security/filter/ROLES`);
        siteRoleOptions.value =
          data?.map((obj) => {
            return {
              label: obj.text,
              value: obj.value,
            };
          }) ?? [];
        showAddSiteRole.value = true;
        const modal = new Modal(
          document.getElementById(
            "vz-add-edit-modal-user_site_modal"
          ) as HTMLElement
        );
        modal.show();
      } catch (e) {
        console.log(e);
      }
    };
    const submitSiteRole = async (reqData) => {
      try {
        showAddSiteRole.value = false;
        const payload = {
          roleId: +reqData.roleId || 0,
          userAccountId: dataProp.value.webId,
          siteId: selectedForSite.value.siteId,
        };
        await SiteApi.getInstance().addUserSiteRole(payload);
        refreshList();
      } catch (e) {
        console.log(e);
      }
    };

    const webId = computed(() => props.data?.webId);
    let lastFetchedWebId = null;

    const debouncedQueryUpdate = debounce(async (webId) => {
      if (webId !== lastFetchedWebId) {
        tableData.value = [];
        allTableData.value = [];
        lastFetchedWebId = webId;
        defaultFilter.value = { ...defaultFilter.value };
        await queryUpdate(+webId, {
          ...defaultFilter.value,
          page: page.value - 1,
          size: INNER_PAGE_ROWS_PER_PAGE,
        });
      }
    }, 300);

    watch(
      () => props.isActive,
      async (isActive) => {
        if (isActive && dataProp.value.webId) {
          // await queryUpdate(dataProp.value.webId, {
          //   page: page.value - 1,
          //   size: rowsPerPage.value,
          // });
          loading.value = true;
          if (Datatable.value?.VZFilter) {
            await Datatable.value.VZFilter.getAppliedFilters();
          }
        } else {
          tableData.value = [];
        }
      }
    );
    const getClass = (role) => {
      let className = "bg-success";
      if (role == "Manager") {
        className = "bg-info";
      } else if (role == "User") {
        className = "bg-primary";
      } else if (role == "Admin") {
        className = "bg-success";
      }
      return className;
    };
    return {
      Datatable,
      currentUser,
      bulkDelete,
      format,
      tableData,
      tableHeader,
      // tableFilter,
      showSearch,
      page,
      total,
      getClass,
      rowsPerPage,
      defaultFilter,
      loading,
      selectedForAction,
      formData,
      syncQuery,
      refreshList,
      submit,
      openDetailModal,
      selectedForSite,
      tabs,
      hasPermission,
      permissionModel,
      t,
      showAddSiteRole,
      openRoleModal,
      userSiteModelData,
      siteRoleOptions,
      computedUserSiteModalData,
      submitSiteRole,
      showDeleteDialog,
      showDetailDialog,
      openDeleteDialog,
    };
  },
});
